import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Router } from '@angular/router';
import { PermissionHandlerService } from '../../services/permission-handler.service';
import { DataService } from '../../services/data.service';
import { GlobalConstants } from 'src/app/services/global-constants';
import { MessageService } from 'primeng/api';
import { TranslatorPipe } from '../../services/translator.pipe';

// Interfaces based on the backend DTOs
export interface Customer {
  name: string;
  email: string;
  phone?: string;
  organization?: string;
  userId?: string;
}

export interface ProductServiceInfo {
  productId: string;
  productName: string;
  productType?: string;
}

export interface Order {
  orderNumber: string;
  customer: Customer;
  productService: ProductServiceInfo;
  attachments?: any[];
  orderHistory?: any[];
  createdBy?: string;
  updatedBy?: string;
}

export interface CreateOrder {
  customer: Customer;
  productService: ProductServiceInfo;
}

@Component({
  selector: 'app-product-orders',
  templateUrl: './product-orders.component.html',
  styleUrl: './product-orders.component.scss'
})
export class ProductOrdersComponent implements OnInit, OnChanges {
  @Input() productService: any = null; // Product service data passed from parent
  @Output() orderSubmit = new EventEmitter<any>();
  @Output() orderCancel = new EventEmitter<void>();

  orderForm: FormGroup;
  isSubmitting = false;

  // Payment method options removed - no longer needed

  // Delivery method options removed - no longer needed


  constructor(
    private fb: FormBuilder,
    public permissionService: PermissionHandlerService,
    private dataService: DataService,
    private router: Router,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {
    this.orderForm = this.fb.group({
      // Customer Information
      customer: this.fb.group({
        name: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        phone: [''],
        organization: [''],
        userId: ['']
      }),

      // Payment Information removed - no longer needed

      // Delivery Information removed - no longer needed
    });
  }

  ngOnInit() {
    console.log('ProductOrdersComponent initialized with service:', this.productService);
    if (this.productService) {
      this.populateProductData();
    }

    this.getSurveyDetails();
    
    // Pricing calculations removed - pricing is now locked
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['productService'] && changes['productService'].currentValue) {
      console.log('Product service changed:', changes['productService'].currentValue);
      this.populateProductData();
    }
  }

  populateProductData() {
    if (this.productService) {
      console.log('Populating product data:', this.productService);
      
      // Extract service type from fields array (for raw API data)
      const serviceTypeField = this.productService.fields?.find((field: any) => field.label === 'Service Type');
      const serviceType = serviceTypeField ? serviceTypeField.data : this.productService.type || '';
      
      // Get product name - try multiple sources
      const productName = this.productService.name || this.productService.title || 'Unknown Service';
      
      console.log('Service data loaded:', {
        productName: productName,
        productType: serviceType,
        productId: this.productService._id
      });
    }
  }

  // Pricing calculation methods removed - pricing is now locked

  // Helper method to get service type from fields
  getServiceType(): string {
    if (!this.productService?.fields) return '';
    
    const serviceTypeField = this.productService.fields.find((field: any) => 
      field.label === 'Service Type' || field.label === 'serviceType'
    );
    
    return serviceTypeField ? serviceTypeField.data : this.productService.type || '';
  }

  // Helper method to get specific service field data
  getServiceField(fieldLabel: string): string {
    if (!this.productService?.fields) return '';
    
    const field = this.productService.fields.find((field: any) => field.label === fieldLabel);
    return field ? field.data : '';
  }

  markSubmitAttempted() {
    // Mark that submit has been attempted
    this.submitAttempted = true;
    // Trigger change detection to update the view
    this.cdr.detectChanges();
    console.log('Submit attempted - highlighting required questions');
  }

  async onSubmit() {
    // Mark submit attempt
    this.submitAttempted = true;
    
    if (this.orderForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      
      try {
        const formValue = this.orderForm.value;
        
        // Prepare the order data - simplified payload
        const userId = localStorage.getItem('uid') || '';
        
        let orderData = {
          customer: {
            name: formValue.customer.name,
            email: formValue.customer.email,
            phone: formValue.customer.phone,
            organization: formValue.customer.organization,
        //    userId: userId
          },
          productId: this.productService?._id || '',
          orderStatus: 'ACTIVE',
          survey: this.validSurvey ? this.survey : null
        };
        this.validSurvey
       // Validate survey questions before submission
       if (this.survey && this.survey.questions) {
         const validationResult = this.validateRequiredQuestions();
         
          if (!validationResult.isValid) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate('Required Questions'),
              detail: this.translate(`Please answer all required questions: ${validationResult.missingQuestions.join(', ')}`)
            });
            this.isSubmitting = false;
            return;
          }
       }


        // Call the data service to create the order
        const result = await this.dataService.postData('orders/', orderData, 'POST');

        if(this.validSurvey) {
          this.surveySubmit();
          
        }
        
        if (result.error) {
          console.error('Error creating order:', result.msg);
          // You can emit an error event or show a toast notification here
          this.orderSubmit.emit({ error: true, message: result.msg });
        } else {
          console.log('Order created successfully:', result.data);
          // Emit success event with the created order data
          this.orderSubmit.emit({ success: true, data: result.data });
          
          // Navigate to order details page
          const userId = localStorage.getItem('uid') || '';
          const customerEmail = formValue.customer.email;
          
          if (userId && customerEmail) {
            this.router.navigate(['/order-details', userId, customerEmail]);
          } else {
            console.warn('Missing userId or email for navigation');
          }
        }
      } catch (error) {
        console.error('Error creating order:', error);
          this.orderSubmit.emit({ error: true, message: this.translate('An unexpected error occurred while creating the order.') });
      } finally {
        this.isSubmitting = false;
      }
    }
  }

  onCancel() {
    this.orderCancel.emit();
  }

  // Helper methods for form validation
  isFieldInvalid(fieldPath: string): boolean {
    const field = this.orderForm.get(fieldPath);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldPath: string): string {
    const field = this.orderForm.get(fieldPath);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) {
        return this.translate('This field is required');
      }
      if (field.errors['email']) {
        return this.translate('Please enter a valid email address');
      }
      if (field.errors['min']) {
        return this.translate(`Value must be at least ${field.errors['min'].min}`);
      }
    }
    return '';
  }

  // Reset form
  resetForm() {
    this.orderForm.reset();
    this.isSubmitting = false;
    if (this.productService) {
      this.populateProductData();
    }
  }

  siteUrl = GlobalConstants.siteUrl;
  openSurveyLink() {
    const surveyLink = this.getServiceField('Survey Link');
    if (surveyLink) {
      window.open( surveyLink, '_blank');
    } else {
      alert(this.translate('No survey link available.'));
    }
  }

  extractSurveyId(link: string): string {
    let surveyId: string = '';
    if (link) {
      const match = link.match(/\/([^\/\?]+)(?:\?|$)/g);
      if (match && match.length > 0) {
        surveyId = match[match.length - 1].replace('/', '').replace('?', '');
      }
    }
    return surveyId;
  }

  validSurvey: boolean = false;
  survey: any;
  submitAttempted: boolean = false;

  getSurveyDetails() {
    const surveyLink = this.getServiceField('Survey Link');
    const surveyId = this.extractSurveyId(surveyLink);
    if (surveyId) {
      this.dataService.getData('survey/id/'+surveyId).then((res: any) => {
        if (res && res.data && res.data._id === surveyId) {
            this.validSurvey = true;
            this.survey = res.data;
            console.log('Survey details fetched:', res.data);
        } 
      }).catch(err => {
        console.error('Error fetching survey:', err);
      });
    }

    
  }

  async surveySubmit() {
    let email = this.orderForm.get('customer.email')?.value;
    if (email) {
      this.survey.email = email;
    }

    let res = await this.dataService.postData(
      `survey/submit`,
      this.survey
    );

  }

  /**
   * Validates that all required questions in the survey are answered
   * @returns Object with validation result and missing questions list
   */
  validateRequiredQuestions(): { isValid: boolean; missingQuestions: string[] } {
    if (!this.survey || !this.survey.questions || !Array.isArray(this.survey.questions)) {
      return { isValid: true, missingQuestions: [] };
    }

    const missingQuestions: string[] = [];

    this.survey.questions.forEach((question: any, index: number) => {

      
      // Check if the question is required (meta.validReq is true)
      if (question.meta && question.meta.validReq === true) {
        // Check if the question has a value and it's not null or empty string
        if ((!question.meta.value || question.meta.value === null || question.meta.value === '') && 
        (!question.meta?.fields[0].value || question.meta?.fields[0].value === null || question.meta?.fields[0].value === '')) {
          // Add question identifier to missing list
          const questionLabel = question.meta.questionCode || question.placeholder || `Question ${index + 1}`;
          if(this.canShowCondQuestion(question)) missingQuestions.push(questionLabel);
        }
      }
    });

    return {
      isValid: missingQuestions.length === 0,
      missingQuestions: missingQuestions
    };
  }

  translate(key: string): string {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }


  canShowCondQuestion(Question: any): boolean {
    
    if (Question.type == 'DYNAMIC_MESSAGE' && this.survey.settings.displayMode == 'Classic')
    {
      return false;
    }
    if (Question.meta.conditional && Question.meta.condition.question != null) {
      for (const question of this.survey.questions) {
        const Ans = this.getAnswerProvided(question);
        if (Ans != undefined && Ans.length > 0) {
          const value =
            Question.meta.condition.question.questionCode ==
            question.questionCode &&
            (Question.meta.condition.option.label == Ans ||
              Ans.includes(Question.meta.condition.option.label));
          if (value) {
            return true;
          }
        }
      }
      this.clearAnswerProvided(Question);
      return false;
    } else {
      return true;
    }
  }

  clearAnswerProvided(question: any): void {
    const metaValueTypes = [
      'SELECT_ONE',
      'DROPDOWN_MENU',
      'SELECT_ONE_IMAGE',
      'SMILE_RATING',
    ];
    const metaValueArrayTypes = ['SELECT_MANY', 'SELECT_MANY_IMAGE'];
  
    if (!question || !question.type || !question.meta) {
      return;
    }
  
    if (metaValueTypes.includes(question.type)) {
      question.meta.value = null;
    }
  
    if (metaValueArrayTypes.includes(question.type)) {
      question.meta.value = [];
    }
  }

  getAnswerProvided(question: any): any {
    let allAnswersProvided = true;
    const metaValueTypes = [
      'SELECT_ONE',
      'DROPDOWN_MENU',
      'SELECT_ONE_IMAGE',
      'SMILE_RATING',
    ];
    const metaValueArrayTypes = ['SELECT_MANY', 'SELECT_MANY_IMAGE'];
    let answerProvided = false;

    if (!question || !question.type) {
      return true;
    }

    if (metaValueTypes.includes(question.type)) {
      if (
        question.meta &&
        question.meta.value !== null &&
        question.meta.value !== undefined &&
        question.meta.value.toString().trim() !== ''
      ) {
        return question.meta.value;
      }
    }

    if (metaValueArrayTypes.includes(question.type)) {
      if (
        question.meta &&
        Array.isArray(question.meta.value) &&
        question.meta.value.length > 0
      ) {
        return question.meta.value;
      }
    }
  }

  /**
   * Check if a question is required and not answered
   * @param question The question to check
   * @returns true if the question is required but not answered
   */
  isQuestionInvalid(question: any): boolean {
    // Always allow highlighting if submit has been attempted
    if (!this.submitAttempted) {
      return false;
    }
    
    // Check if question should be shown
    if (!this.canShowCondQuestion(question)) {
      return false;
    }
    
    // Check if the question is required
    if (question.meta && question.meta.validReq === true) {
      // Check if the question has a value
      let hasMetaValue = false;
      if (question.meta.value !== null && question.meta.value !== undefined && question.meta.value !== '') {
        if (Array.isArray(question.meta.value)) {
          hasMetaValue = question.meta.value.length > 0;
        } else {
          hasMetaValue = true;
        }
      }
      
      // Check if fields have values (for text inputs, textareas, etc.)
      let hasFieldValue = false;
      if (question.meta.fields && Array.isArray(question.meta.fields)) {
        hasFieldValue = question.meta.fields.some((field: any) => {
          if (field.value !== null && field.value !== undefined && field.value !== '') {
            if (Array.isArray(field.value)) {
              return field.value.length > 0;
            }
            return true;
          }
          return false;
        });
      }
      
      // Return true if question is required and has no value
      const isInvalid = !hasMetaValue && !hasFieldValue;
      
      // Debug logging
      if (isInvalid) {
        console.log('Invalid question:', {
          questionText: question.questionText,
          questionType: question.type,
          hasMetaValue,
          hasFieldValue,
          metaValue: question.meta.value,
          fields: question.meta.fields
        });
      }
      
      return isInvalid;
    }
    
    return false;
  }

  /**
   * Check if a question is required
   * @param question The question to check
   * @returns true if the question is required
   */
  isQuestionRequired(question: any): boolean {
    return question.meta && question.meta.validReq === true;
  }

  /**
   * Check if a price value is numeric
   * @param value The price value (number or string)
   * @returns true if the value is a valid number
   */
  isNumericPrice(value: number | string | undefined): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === 'number') return true;
    if (typeof value === 'string') {
      // Check if string is a valid number (including decimals)
      const numValue = parseFloat(value);
      return !isNaN(numValue) && isFinite(numValue) && value.trim() !== '';
    }
    return false;
  }

  /**
   * Format price for display - shows number with currency or text as-is
   * @param amount The price amount (number or string)
   * @param currency The currency symbol
   * @returns Formatted price string
   */
  formatPriceDisplay(amount: number | string | undefined, currency: string = 'SAR'): string {
    if (amount === undefined || amount === null) return '';
    if (this.isNumericPrice(amount)) {
      const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
    }
    // Return text as-is if not numeric
    return String(amount);
  }
}
